import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import { formatNumeric } from '@/lib/utils/stringUtils'
import { formatCurrency, privatize } from '@/lib/utils/revenueUtils'
import type { SourceRow } from './hooks/useAttributionData'

interface SourcesTableProps {
  sourceData: SourceRow[]
  searchTerm: string
  connectorType: 'stripe' | 'foxy' | null
  privacyMode?: boolean
}

export default function SourcesTable({ sourceData, searchTerm, connectorType, privacyMode = false }: SourcesTableProps) {
  return (
    <div className="responsive-table-wrapper">
      <Table>
        <TableHeader>
          <TableRow className="border-b border-border/60 hover:bg-transparent">
            <TableHead className="data-table-th min-w-[200px]">Source</TableHead>
            <TableHead className="data-table-th text-right min-w-[100px]">
              <Tooltip>
                <TooltipTrigger className="flex items-center gap-1 justify-end cursor-help">Unique visitors</TooltipTrigger>
                <TooltipContent>Unique visitors from this UTM source</TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className="data-table-th text-right min-w-[120px]">
              <Tooltip>
                <TooltipTrigger className="flex items-center justify-end cursor-help">Attributed revenue</TooltipTrigger>
                <TooltipContent className="max-w-xs">Revenue from {connectorType || 'payment'} transactions linked to visitors from this source</TooltipContent>
              </Tooltip>
            </TableHead>
            <TableHead className="data-table-th text-right min-w-[100px]">
              <Tooltip>
                <TooltipTrigger className="flex items-center gap-1 justify-end cursor-help">Rev / Visitor</TooltipTrigger>
                <TooltipContent>Average attributed revenue per visitor from this source</TooltipContent>
              </Tooltip>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sourceData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="text-center py-12 text-muted-foreground/70">
                {searchTerm ? 'No sources match your search' : 'No UTM source data available for this period.'}
              </TableCell>
            </TableRow>
          ) : (
            (() => {
              const maxSessions = Math.max(...sourceData.map((s) => s.sessions), 1)
              return sourceData
                .filter((s) => !searchTerm || s.source.toLowerCase().includes(searchTerm.toLowerCase()))
                .slice(0, 20)
                .map((source, idx) => {
                  const revDollars = source.revenueCents / 100
                  const revPerSession = source.sessions > 0 ? revDollars / source.sessions : 0
                  const sessionPct = (source.sessions / maxSessions) * 100
                  return (
                    <TableRow key={idx} className="data-table-row border-b border-border hover:bg-muted/50">
                      <TableCell className="data-table-cell data-table-cell-primary">{source.source}</TableCell>
                      <TableCell className="data-table-cell data-table-cell-numeric">
                        <div className="flex flex-col items-end gap-1">
                          <span>{formatNumeric(source.sessions)}</span>
                          <div className="w-16 h-1 rounded-full bg-muted/40 overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${Math.max(2, sessionPct)}%`, backgroundColor: 'var(--chart-1)', opacity: 0.5 }} />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="data-table-cell data-table-cell-numeric">
                        {revDollars > 0 ? <span className="font-medium text-foreground">{privatize(formatCurrency(revDollars), privacyMode)}</span> : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="data-table-cell data-table-cell-numeric">
                        {revPerSession > 0 ? privatize(`$${revPerSession.toFixed(2)}`, privacyMode) : '—'}
                      </TableCell>
                    </TableRow>
                  )
                })
            })()
          )}
        </TableBody>
      </Table>
    </div>
  )
}
